// features/unified-learning/runtime/screens/PathCreateScreen.tsx
// Course creation wizard: topic → level → time → content → generate
//
// UX direction: get the user from zero to "I understand what this builds"
// as fast as possible. Every step must communicate value, not just collect data.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  ScrollView,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
  KeyboardAvoidingView,
} from 'react-native';
import { router } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { ensureKursiPlayOpenAccessSession } from '@/utils/kursiPlay';
import { AuthPopup } from '@/components/AuthPopup';
import { ClickSpark, Stepper } from '@/components/learn-premium/PremiumPrimitives';

// ── Design tokens ─────────────────────────────────────────────────────────────
const PRIMARY    = theme.colors.primary;
const BG         = '#0F0F1A';
const SURFACE2   = '#2D2B50';
const TEXT       = '#FFFFFF';
const TEXT_MED   = '#A0A0B8';
const TEXT_MUTED = '#6B6B85';
const GREEN      = '#10B981';
const AMBER      = '#F59E0B';
const TOTAL_STEPS = 4;
const PREMIUM_BG = '#070B16';
const PREMIUM_CARD = '#0E1428';
const PREMIUM_CARD_2 = '#131C34';
const PREMIUM_BORDER = '#273252';
const CREATE_DRAFT_KEY = 'kursi_learn_create_draft_v1';

// ── Option data ───────────────────────────────────────────────────────────────

const LEVEL_OPTIONS = [
  {
    value: 'beginner',
    label: 'מתחיל',
    profile: 'מתאים ללומדים חדשים בנושא',
    desc: 'נבנה בסיס ברור, קצב נוח, והסברים פשוטים יותר.',
    cue: 'מתאים אם זו החשיפה הראשונה לנושא.',
  },
  {
    value: 'intermediate',
    label: 'בינוני',
    profile: 'מתאים למי שכבר מכיר את היסודות',
    desc: 'נתקדם מהר יותר ונשלב תרגול מעשי והעמקה.',
    cue: 'מתאים אם כבר יש ניסיון בסיסי וצריך לדייק ביצוע.',
  },
  {
    value: 'advanced',
    label: 'מתקדם',
    profile: 'מתאים ללומדים עם רקע קודם',
    desc: 'נתמקד בחידוד, תרגול מאתגר, ויישום ברמה גבוהה יותר.',
    cue: 'מתאים אם המטרה היא חדות מקצועית ויישום מתקדם.',
  },
];

const TIME_OPTIONS = [
  {
    value: '5',
    pace: 'קצר',
    label: '5 דקות',
    fit: 'מתאים ללמידה מהירה וממוקדת',
    details: 'שיעורים קצרים, קצב גבוה, משימות קטנות יותר',
  },
  {
    value: '10',
    pace: 'מאוזן',
    label: '10 דקות',
    fit: 'מתאים לרוב הלומדים',
    details: 'שילוב טוב בין הבנה, תרגול וקצב נוח',
  },
  {
    value: '20',
    pace: 'מעמיק',
    label: '20+ דקות',
    fit: 'מתאים ללמידה יסודית יותר',
    details: 'שיעורים ארוכים יותר, יותר עומק ויותר רצף בכל נושא',
  },
];

const LANGUAGE_OPTIONS = [
  { value: 'he', label: 'עברית' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'عربية' },
];

// ── Step progress bar ─────────────────────────────────────────────────────────

function StepBar({ current, total }: { current: number; total: number }) {
  return <Stepper step={current} total={total} />;
}

// ── Time pace card ────────────────────────────────────────────────────────────

function TimePaceCard({
  pace, label, fit, details, selected, onPress,
}: {
  pace: string;
  label: string;
  fit: string;
  details: string;
  selected: boolean; onPress: () => void;
}) {
  return (
    <Pressable
      style={[tc.card, selected && tc.cardActive]}
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
    >
      <View style={tc.topRow}>
        <Text style={[tc.badge, selected && tc.badgeActive]}>{pace}</Text>
        <Text style={[tc.check, selected && tc.checkActive]}>{selected ? '✓' : '○'}</Text>
      </View>

      <View style={tc.durationRow}>
        <Text style={[tc.duration, selected && tc.durationActive]}>{label}</Text>
      </View>

      <Text style={[tc.fit, selected && tc.fitActive]}>{fit}</Text>
      <Text style={tc.details}>{details}</Text>
    </Pressable>
  );
}

const tc = StyleSheet.create({
  card: {
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: '#33446F',
    backgroundColor: '#101A35',
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 8,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  cardActive: {
    borderColor: '#8D88FF',
    backgroundColor: '#1A2150',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 16px 36px rgba(8,12,28,0.52), 0 0 0 1px rgba(141,136,255,0.35)' } as any) : {}),
  },
  topRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badge: {
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: '#CFD8F2',
    backgroundColor: '#1B2748',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#3A4B72',
    paddingHorizontal: 10,
    paddingVertical: 4,
    overflow: 'hidden',
  },
  badgeActive: {
    color: '#F2F0FF',
    backgroundColor: '#2D3670',
    borderColor: '#6D68FF',
  },
  check: {
    fontFamily: theme.fonts.bold,
    fontSize: 18,
    color: '#8695BE',
  },
  checkActive: { color: '#B6B2FF' },
  durationRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  duration: {
    fontFamily: theme.fonts.bold,
    fontSize: 28,
    color: '#EEF3FF',
    textAlign: 'right',
  },
  durationActive: { color: '#FFFFFF' },
  fit: {
    fontFamily: theme.fonts.medium,
    fontSize: 14,
    color: '#D6E0FB',
    textAlign: 'right',
    lineHeight: 21,
  },
  fitActive: { color: '#EEF2FF' },
  details: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: '#AFC0E8',
    textAlign: 'right',
    lineHeight: 20,
  },
});

function LevelCard({
  label,
  profile,
  desc,
  cue,
  selected,
  onPress,
}: {
  label: string;
  profile: string;
  desc: string;
  cue: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      style={[lc.card, selected && lc.cardActive]}
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
    >
      <View style={lc.topRow}>
        <Text style={[lc.check, selected && lc.checkActive]}>{selected ? '✓' : '○'}</Text>
        <Text style={[lc.title, selected && lc.titleActive]}>{label}</Text>
      </View>
      <Text style={lc.profile}>{profile}</Text>
      <Text style={lc.desc}>{desc}</Text>
      <View style={[lc.cuePill, selected && lc.cuePillActive]}>
        <Text style={[lc.cueText, selected && lc.cueTextActive]}>{cue}</Text>
      </View>
    </Pressable>
  );
}

const lc = StyleSheet.create({
  card: {
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: '#344067',
    backgroundColor: '#121B35',
    padding: 16,
    gap: 10,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  cardActive: {
    borderColor: '#8B87FF',
    backgroundColor: '#1A2150',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 0 0 1px rgba(139,135,255,0.35), 0 16px 36px rgba(12,17,43,0.45)' } as any) : {}),
  },
  topRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontFamily: theme.fonts.bold,
    fontSize: 20,
    color: '#F8FAFF',
    textAlign: 'right',
  },
  titleActive: { color: '#FFFFFF' },
  check: {
    fontFamily: theme.fonts.bold,
    fontSize: 18,
    color: '#8E9BC2',
  },
  checkActive: { color: '#A6A4FF' },
  profile: {
    fontFamily: theme.fonts.medium,
    fontSize: 14,
    color: '#D8E1FB',
    textAlign: 'right',
    lineHeight: 21,
  },
  desc: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: '#B9C5E9',
    textAlign: 'right',
    lineHeight: 20,
  },
  cuePill: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#3D4A71',
    backgroundColor: '#1B2645',
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignSelf: 'flex-end',
  },
  cuePillActive: {
    borderColor: '#6762FF',
    backgroundColor: '#262C5D',
  },
  cueText: {
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: '#BFCBEA',
    textAlign: 'right',
  },
  cueTextActive: { color: '#E6E8FF' },
});

// ── Language row ──────────────────────────────────────────────────────────────

function LangRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <View style={lr.row}>
      {LANGUAGE_OPTIONS.map((opt) => (
        <Pressable
          key={opt.value}
          style={[lr.chip, value === opt.value && lr.chipActive]}
          onPress={() => onChange(opt.value)}
          accessibilityRole="radio"
          accessibilityState={{ checked: value === opt.value }}
        >
          <Text style={[lr.chipText, value === opt.value && lr.chipTextActive]}>
            {opt.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}

const lr = StyleSheet.create({
  row: {
    flexDirection: 'row-reverse',
    gap: 8,
    backgroundColor: '#0A1020',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: PREMIUM_BORDER,
    padding: 6,
  },
  chip: {
    flex: 1,
    borderWidth: 1,
    borderColor: 'transparent',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  chipActive: { borderColor: `${PRIMARY}55`, backgroundColor: `${PRIMARY}2B` },
  chipText: { color: '#AAB6D8', fontFamily: theme.fonts.medium, fontSize: 14 },
  chipTextActive: { color: '#F8FAFF' },
});

// ── Error box ─────────────────────────────────────────────────────────────────

function ErrorBox({ msg }: { msg: string }) {
  return (
    <View style={{ borderRadius: 12, borderWidth: 1, borderColor: '#7F1D1D', backgroundColor: '#3B0A0A', padding: 10 }}>
      <Text style={{ color: '#FCA5A5', textAlign: 'right', fontFamily: theme.fonts.medium, fontSize: 13 }}>{msg}</Text>
    </View>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────

export function PathCreateScreen() {
  const [step, setStep]               = useState(1);

  // Step 1 — Topic & Language
  const [topic, setTopic]             = useState('');
  const [language, setLanguage]       = useState('he');

  // Step 2 — Level & Goal
  const [level, setLevel]             = useState('');
  const [goal, setGoal]               = useState('');

  // Step 3 — Time
  const [timePerSession, setTime]     = useState('');

  // Step 4 — Content
  const [rawContent, setRawContent]   = useState('');
  // TASK 9: PDF tab state
  const [contentTab, setContentTab]   = useState<'text' | 'pdf'>('text');
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [pdfStatus, setPdfStatus]     = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const fileInputRef                  = useRef<HTMLInputElement | null>(null);

  const [error, setError]             = useState<string | null>(null);
  const [submitting, setSubmitting]   = useState(false);
  const [resolvedUserId, setResolvedUserId] = useState<string>('');

  // Auth gate state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthPopup, setShowAuthPopup]   = useState(false);

  // Resolve user_id and auth status on mount
  useEffect(() => {
    async function resolveUser() {
      await ensureKursiPlayOpenAccessSession(supabase);
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        setResolvedUserId(user.id);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    }
    resolveUser();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    try {
      const raw = window.localStorage.getItem(CREATE_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as Partial<{
        topic: string;
        language: string;
        level: string;
        goal: string;
        timePerSession: string;
        rawContent: string;
      }>;
      if (draft.topic) setTopic(draft.topic);
      if (draft.language) setLanguage(draft.language);
      if (draft.level) setLevel(draft.level);
      if (draft.goal) setGoal(draft.goal);
      if (draft.timePerSession) setTime(draft.timePerSession);
      if (draft.rawContent) setRawContent(draft.rawContent);
    } catch {
      // ignore invalid draft payload
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const payload = JSON.stringify({ topic, language, level, goal, timePerSession, rawContent });
    window.localStorage.setItem(CREATE_DRAFT_KEY, payload);
  }, [topic, language, level, goal, timePerSession, rawContent]);

  const clearError = () => setError(null);

  // Handle PDF file selection and extraction
  // Flow: validate → upload to storage → invoke edge function → extract text → delete from storage
  const handlePdfFile = useCallback(async (file: File) => {
    if (!file || file.type !== 'application/pdf') {
      setPdfStatus({ type: 'error', msg: 'נא לבחור קובץ PDF בלבד' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPdfStatus({ type: 'error', msg: 'הקובץ גדול מדי — מקסימום 10MB' });
      return;
    }

    setPdfLoading(true);
    setPdfStatus(null);

    try {
      // Step 1: Upload to temp storage
      const fileName = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.pdf`;

      const { error: uploadErr } = await supabase.storage
        .from('kp-tmp-uploads')
        .upload(fileName, file, { contentType: 'application/pdf', upsert: false });

      if (uploadErr) {
        throw new Error(`שגיאת העלאה: ${uploadErr.message}`);
      }

      // Step 2: Extract text via edge function (unpdf — Deno-compatible, no Worker issues)
      const { data: extractResult, error: fnErr } = await supabase.functions.invoke(
        'kp-extract-pdf-text',
        { body: { file_path: fileName } },
      );

      // Surface specific errors when available
      if (fnErr || !extractResult?.extracted_text) {
        const specificError =
          extractResult?.error ??
          fnErr?.message ??
          'לא הצלחנו לחלץ טקסט מה-PDF. נסה קובץ PDF אחר, או העתק את התוכן ידנית.';
        throw new Error(specificError);
      }

      setRawContent(extractResult.extracted_text);
      setPdfStatus({
        type: 'success',
        msg: `${extractResult.page_count} עמודים חולצו — ${extractResult.char_count.toLocaleString()} תווים`,
      });
      setContentTab('text');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'שגיאה בעיבוד ה-PDF';
      setPdfStatus({ type: 'error', msg });
    } finally {
      setPdfLoading(false);
    }
  }, []);

  const handleNext = useCallback(() => {
    clearError();

    if (step === 1) {
      if (!topic.trim()) { setError('נא להזין נושא ללמידה'); return; }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!level) { setError('נא לבחור רמת ידע כדי שנוכל להתאים את המסלול'); return; }
      setStep(3);
      return;
    }
    if (step === 3) {
      if (!timePerSession) { setError('נא לבחור כמה זמן יש לך לכל שיעור'); return; }
      setStep(4);
      return;
    }
    if (step === 4) {
      if (!isAuthenticated) {
        setShowAuthPopup(true);
        return;
      }
      if (rawContent.trim().length < 50) {
        setError('נא להזין לפחות 50 תווים של חומר לימוד');
        return;
      }
      if (!resolvedUserId) {
        setError('מזהה משתמש לא נטען. נסה לרענן את הדף.');
        return;
      }

      setSubmitting(true);
      const lvlLabel = level === 'beginner' ? 'רמת מתחיל'
        : level === 'intermediate' ? 'רמה בינונית'
        : level === 'advanced' ? 'רמה מתקדמת'
        : '';
      const worldName = `${topic.trim()}${lvlLabel ? ` — ${lvlLabel}` : ''}`.slice(0, 80);
      const learningGoalParts: string[] = [];
      if (goal.trim()) learningGoalParts.push(goal.trim());
      if (lvlLabel) learningGoalParts.push(`רמה: ${lvlLabel}`);
      if (timePerSession) learningGoalParts.push(`זמן פר שיעור: ${timePerSession} דקות`);

      const params = new URLSearchParams({
        world_name:       worldName,
        raw_content:      rawContent.trim(),
        learning_goal:    learningGoalParts.join(' | '),
        language,
        user_id:          resolvedUserId,
        // TASK 5: pass level + time_per_session for adaptive generation
        level,
        time_per_session: timePerSession,
      });
      router.push(`/learn/path/generate?${params.toString()}` as any);
      if (Platform.OS === 'web') {
        window.localStorage.removeItem(CREATE_DRAFT_KEY);
      }
    }
  }, [step, topic, language, level, goal, timePerSession, rawContent, resolvedUserId, isAuthenticated]);

  const handleBack = useCallback(() => {
    clearError();
    if (step > 1) setStep(step - 1);
    else router.back();
  }, [step]);

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: BG }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* Auth gate: show popup for unauthenticated users */}
      <AuthPopup
        visible={showAuthPopup}
        returnTo="/learn/path/create"
        allowDismiss
        onClose={() => {
          // Intentional fallback: if the user leaves this conversion step without auth,
          // send them back to the previous screen instead of keeping them on a blocked action.
          if (!isAuthenticated) {
            router.back();
          } else {
            setShowAuthPopup(false);
          }
        }}
      />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={s.container}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={s.bgLayer} pointerEvents="none">
          <LinearGradient
            colors={['rgba(90,74,255,0.20)', 'rgba(16,20,43,0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={s.topGlow}
          />
          <LinearGradient
            colors={['rgba(76,157,255,0.16)', 'rgba(8,11,20,0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={s.bottomGlow}
          />
        </View>

        {/* Header */}
        <View style={s.header}>
          <Pressable style={s.closeBtn} onPress={handleBack} accessibilityRole="button">
            <Text style={s.closeBtnText}>←</Text>
          </Pressable>
          <View style={{ flex: 1 }}>
            <Text style={s.headerSub}>שלב {step} מתוך {TOTAL_STEPS}</Text>
            <Text style={s.headerTitle}>
              {step === 1
                ? 'על מה נבנה את חוויית הלמידה?'
                : step === 2
                  ? 'לאיזו רמה המסלול צריך להתאים?'
                  : step === 3
                    ? 'כמה זמן מתאים לכל שיעור?'
                  : 'יצירת חוויה לימודית'}
            </Text>
            <Text style={s.headerHint}>
              {step === 1
                ? 'כתוב נושא, הדבק חומר קיים, או תאר בקצרה מה הלומדים צריכים לדעת — ואנחנו נבנה מזה מסלול למידה חכם.'
                : step === 2
                  ? 'הרמה עוזרת לנו להתאים את השיעורים, התרגול והקצב — כדי שהמסלול ירגיש מדויק כבר מהשיעור הראשון.'
                  : step === 3
                    ? 'הבחירה הזו תשפיע על אורך השיעורים, קצב ההתקדמות ועומק התרגול.'
                    : 'מתקדמים שלב אחר שלב לבניית מסלול למידה מותאם.'}
            </Text>
          </View>
        </View>

        <StepBar current={step} total={TOTAL_STEPS} />

        {/* ── Step 1: Topic & Language ── */}
        {step === 1 && (
          <View style={s.premiumCard}>
            <View style={s.stepBody}>
              <View style={s.fieldGroup}>
                <Text style={s.fieldLabel}>נושא או חומר לימוד</Text>
                <TextInput
                  style={s.heroInput}
                  value={topic}
                  onChangeText={setTopic}
                  placeholder="כתוב נושא, הדבק תוכן קיים, או תאר בקצרה מה הלומדים צריכים לדעת."
                  placeholderTextColor="#7B86A8"
                  textAlign="right"
                  maxLength={600}
                  multiline
                  numberOfLines={6}
                  textAlignVertical="top"
                  returnKeyType="done"
                />
              </View>

              <View style={s.fieldGroup}>
                <Text style={s.fieldLabel}>שפת המסלול</Text>
                <LangRow value={language} onChange={setLanguage} />
              </View>

              <View style={s.outputPreview}>
                <Text style={s.outputTitle}>מה המערכת תבנה עבורך</Text>
                <View style={s.outputGrid}>
                  <View style={s.outputCell}>
                    <Text style={s.outputCellIcon}>📘</Text>
                    <Text style={s.outputCellTitle}>שיעורים מובנים מתוך החומר</Text>
                    <Text style={s.outputCellDesc}>חלוקה ברורה לנושאים, לפי סדר לימוד הגיוני.</Text>
                  </View>
                  <View style={s.outputCell}>
                    <Text style={s.outputCellIcon}>✍️</Text>
                    <Text style={s.outputCellTitle}>תרגול לפי הנושאים המרכזיים</Text>
                    <Text style={s.outputCellDesc}>שאלות ותרגול ממוקד כדי להפוך ידע לביצוע.</Text>
                  </View>
                  <View style={s.outputCell}>
                    <Text style={s.outputCellIcon}>↺</Text>
                    <Text style={s.outputCellTitle}>סקירת טעויות עם ניסיון חוזר</Text>
                    <Text style={s.outputCellDesc}>זיהוי נקודות חלשות וחזרה ממוקדת עד לשיפור.</Text>
                  </View>
                  <View style={s.outputCell}>
                    <Text style={s.outputCellIcon}>▲</Text>
                    <Text style={s.outputCellTitle}>תמונת התקדמות ומוכנות</Text>
                    <Text style={s.outputCellDesc}>מדדי התקדמות ברורים כדי לדעת מה מוכן ומה דורש תרגול.</Text>
                  </View>
                </View>
              </View>

              <View style={s.trustStrip}>
                <Text style={s.trustText}>אפשר לערוך ולדייק את המבנה בהמשך — המערכת תייצר קודם מבנה ראשוני לפני פרסום.</Text>
              </View>

              <View style={s.step1CtaRow}>
                <Pressable style={s.step1BackBtn} onPress={handleBack} accessibilityRole="button">
                  <Text style={s.step1BackBtnText}>חזרה</Text>
                </Pressable>
                <ClickSpark onPress={handleNext} disabled={submitting}>
                  <View style={[s.step1PrimaryBtn, submitting && s.primaryBtnDisabled]}>
                    {submitting
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={s.step1PrimaryBtnText}>המשך לבניית המסלול</Text>}
                  </View>
                </ClickSpark>
              </View>
            </View>
          </View>
        )}

        {/* ── Step 2: Level & Goal ── */}
        {step === 2 && (
          <View style={s.levelStepCard}>
            <View style={s.levelSectionHeader}>
              <Text style={s.levelStepLabel}>הגדרת רמת הלומדים</Text>
              <Text style={s.levelStepTitle}>מה רמת הידע של הלומדים?</Text>
              <Text style={s.levelStepHint}>
                הבחירה כאן מגדירה את עומק ההסברים, קצב ההתקדמות ורמת האתגר בתרגול.
              </Text>
            </View>

            <View style={s.levelCardsWrap}>
              {LEVEL_OPTIONS.map((opt) => (
                <LevelCard
                  key={opt.value}
                  label={opt.label}
                  profile={opt.profile}
                  desc={opt.desc}
                  cue={opt.cue}
                  selected={level === opt.value}
                  onPress={() => setLevel(opt.value)}
                />
              ))}
            </View>

            <View style={s.levelGoalSection}>
              <Text style={s.levelGoalLabel}>מטרה נוספת למסלול (אופציונלי)</Text>
              <TextInput
                style={s.levelGoalInput}
                value={goal}
                onChangeText={setGoal}
                placeholder="לדוגמה: להתכונן לראיון עבודה, לעבור מבחן, לחזק בסיס מקצועי"
                placeholderTextColor="#8490B8"
                textAlign="right"
                maxLength={220}
              />
            </View>

            <View style={s.levelReassurance}>
              <Text style={s.levelReassuranceText}>זו רק נקודת התחלה — נוכל לדייק את המסלול בהמשך.</Text>
            </View>

            <View style={s.levelActions}>
              <Pressable style={s.levelBackBtn} onPress={handleBack} accessibilityRole="button">
                <Text style={s.levelBackBtnText}>חזרה</Text>
              </Pressable>
              <ClickSpark onPress={handleNext} disabled={submitting}>
                <View style={[s.levelNextBtn, submitting && s.primaryBtnDisabled]}>
                  {submitting
                    ? <ActivityIndicator color="#fff" size="small" />
                    : <Text style={s.levelNextBtnText}>המשך להגדרת המסלול</Text>}
                </View>
              </ClickSpark>
            </View>
          </View>
        )}

        {/* ── Step 3: Time ── */}
        {step === 3 && (
          <View style={s.timeStepCard}>
            <View style={s.timeStepHeader}>
              <Text style={s.timeStepLabel}>הגדרת קצב למידה</Text>
              <Text style={s.timeStepTitle}>איזה קצב למידה מתאים ללומדים?</Text>
              <Text style={s.timeStepHint}>
                נבחר את משך השיעור כדי להתאים את הקצב והמבנה ללומדים.
              </Text>
            </View>

            <View style={s.timeCardsWrap}>
              {TIME_OPTIONS.map((opt) => (
                <TimePaceCard
                  key={opt.value}
                  pace={opt.pace}
                  label={opt.label}
                  fit={opt.fit}
                  details={opt.details}
                  selected={timePerSession === opt.value}
                  onPress={() => setTime(opt.value)}
                />
              ))}
            </View>

            <View style={s.timeReassurance}>
              <Text style={s.timeReassuranceText}>
                זו רק נקודת פתיחה — נוכל לדייק את אורך השיעורים בהמשך.
              </Text>
            </View>

            <View style={s.timeActions}>
              <Pressable style={s.timeBackBtn} onPress={handleBack} accessibilityRole="button">
                <Text style={s.timeBackBtnText}>חזרה</Text>
              </Pressable>
              <ClickSpark onPress={handleNext} disabled={submitting}>
                <View style={[s.timeNextBtn, submitting && s.primaryBtnDisabled]}>
                  {submitting
                    ? <ActivityIndicator color="#fff" size="small" />
                    : <Text style={s.timeNextBtnText}>המשך להגדרת המסלול</Text>}
                </View>
              </ClickSpark>
            </View>
          </View>
        )}

        {/* ── Step 4: Content ── */}
        {step === 4 && (
          <View style={s.contentStepCard}>
            <View style={s.contentStepHeader}>
              <Text style={s.contentStepLabel}>שלב אחרון · חומר לימוד</Text>
              <Text style={s.contentStepTitle}>ממה נבנה את חוויית הלמידה?</Text>
              <Text style={s.contentStepHint}>
                אפשר להדביק טקסט או להעלות PDF — ואנחנו נהפוך את החומר לשיעורים, תרגול, סקירת טעויות ומדדי התקדמות.
              </Text>
            </View>
            {!isAuthenticated && (
              <View style={s.authCheckpointCard}>
                <Text style={s.authCheckpointTitle}>לפני יצירה: מתחברים לשמירת המסלול</Text>
                <Text style={s.authCheckpointBody}>
                  כדי להמשיך ליצירת המסלול צריך להתחבר. כל מה שכבר מילאת נשמר, ואחרי ההתחברות ממשיכים בדיוק מכאן.
                </Text>
                <Pressable style={s.authCheckpointBtn} onPress={() => setShowAuthPopup(true)} accessibilityRole="button">
                  <Text style={s.authCheckpointBtnText}>התחבר כדי להמשיך</Text>
                </Pressable>
              </View>
            )}

            <View style={s.contentModeSelector}>
              <Pressable
                style={[s.contentModeCard, contentTab === 'text' && s.contentModeCardActive]}
                onPress={() => setContentTab('text')}
                accessibilityRole="radio"
                accessibilityState={{ checked: contentTab === 'text' }}
              >
                <Text style={[s.contentModeTitle, contentTab === 'text' && s.contentModeTitleActive]}>הדבק טקסט</Text>
                <Text style={s.contentModeDesc}>מתאים לסיכום קצר, טיוטה או תוכן שערכת ידנית</Text>
              </Pressable>
              {Platform.OS === 'web' && (
                <Pressable
                  style={[s.contentModeCard, contentTab === 'pdf' && s.contentModeCardActive]}
                  onPress={() => setContentTab('pdf')}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: contentTab === 'pdf' }}
                >
                  <Text style={[s.contentModeTitle, contentTab === 'pdf' && s.contentModeTitleActive]}>העלה PDF</Text>
                  <Text style={s.contentModeDesc}>מתאים לסיכום, מאמר או חומר קורס מוכן</Text>
                </Pressable>
              )}
            </View>
            <Text style={s.contentModeHelper}>אפשר להתחיל גם מחומר לא מושלם — תמיד ניתן לערוך ולדייק בהמשך.</Text>

            <View style={s.contentInputSection}>
              <Text style={s.contentInputLabel}>טקסט או חומר לימוד</Text>
              {contentTab === 'text' && (
                <>
                  <TextInput
                    style={s.contentTextarea}
                    value={rawContent}
                    onChangeText={setRawContent}
                    placeholder="הדבק כאן את חומר הלימוד — למשל סיכום, מאמר, תוכן קורס או תיאור של מה הלומדים צריכים לדעת."
                    placeholderTextColor="#8392B9"
                    textAlign="right"
                    multiline
                    numberOfLines={13}
                    textAlignVertical="top"
                  />
                  <View style={s.charCountRow}>
                    <Text style={s.charCount}>{rawContent.length.toLocaleString()} תווים</Text>
                    {rawContent.length >= 50 && rawContent.length < 500 && (
                      <Text style={[s.charCountHint, { color: AMBER }]}>יש בסיס טוב — עוד קצת תוכן ייתן תוצאה מדויקת יותר</Text>
                    )}
                    {rawContent.length >= 500 && (
                      <Text style={[s.charCountHint, { color: GREEN }]}>✓ מעולה — יש מספיק תוכן לבניית מסלול מלא</Text>
                    )}
                    {rawContent.length < 50 && rawContent.length > 0 && (
                      <Text style={[s.charCountHint, { color: TEXT_MUTED }]}>מינימום 50 תווים כדי להתחיל יצירה</Text>
                    )}
                  </View>
                </>
              )}

              {/* PDF upload tab — web only */}
              {contentTab === 'pdf' && Platform.OS === 'web' && (
                <View style={s.pdfArea}>
                  {/* Hidden native file input for web */}
                  {/* @ts-ignore */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e: any) => {
                      const file = e.target?.files?.[0];
                      if (file) void handlePdfFile(file);
                      e.target.value = '';
                    }}
                  />

                  {pdfLoading ? (
                    <View style={s.pdfLoadingState}>
                      <ActivityIndicator color={PRIMARY} size="large" />
                      <Text style={s.pdfLoadingTitle}>מחלץ טקסט מה-PDF...</Text>
                      <Text style={s.pdfLoadingHint}>זה לוקח כמה שניות</Text>
                    </View>
                  ) : pdfStatus?.type === 'success' ? (
                    <View style={s.pdfSuccessState}>
                      <View style={s.pdfSuccessIcon}>
                        <Text style={{ fontSize: 28 }}>✓</Text>
                      </View>
                      <Text style={s.pdfSuccessTitle}>הקובץ מוכן ליצירת מסלול</Text>
                      <Text style={s.pdfSuccessMsg}>{pdfStatus.msg}</Text>
                      <Text style={s.pdfSuccessHint}>
                        הטקסט שחולץ הועבר אוטומטית לשדה התוכן — אפשר לערוך לפני המשך
                      </Text>
                      <Pressable
                        style={s.pdfReplaceBtn}
                        onPress={() => {
                          if (fileInputRef.current) (fileInputRef.current as any).click();
                        }}
                      >
                        <Text style={s.pdfReplaceBtnText}>העלה קובץ אחר</Text>
                      </Pressable>
                    </View>
                  ) : (
                    <Pressable
                      style={s.pdfDropZone}
                      onPress={() => {
                        if (!pdfLoading && fileInputRef.current) {
                          (fileInputRef.current as any).click();
                        }
                      }}
                      accessibilityRole="button"
                    >
                      <Text style={s.pdfDropIcon}>📄</Text>
                      <Text style={s.pdfDropTitle}>בחר קובץ PDF</Text>
                      <Text style={s.pdfDropHint}>עד 10MB · הטקסט יחולץ אוטומטית ויתווסף לעריכה</Text>
                      {pdfStatus?.type === 'error' && (
                        <View style={s.pdfErrorBanner}>
                          <Text style={s.pdfErrorIcon}>⚠</Text>
                          <Text style={s.pdfErrorText}>{pdfStatus.msg}</Text>
                          <Text style={s.pdfErrorAction}>לחץ לנסות שוב, או עבור להדבקת טקסט ידנית</Text>
                        </View>
                      )}
                    </Pressable>
                  )}
                </View>
              )}
            </View>

            <View style={s.transformationPreview}>
              <Text style={s.transformationTitle}>מה המערכת תבנה עבורך</Text>
              <View style={s.previewTopRow}>
                <View style={s.previewHeroBlock}>
                  <Text style={s.previewHeroKicker}>תוצר מרכזי</Text>
                  <Text style={s.previewHeroTitle}>
                    {timePerSession === '5' ? '2' : timePerSession === '20' ? '4' : '3'} שיעורים מובנים עם רצף התקדמות ברור
                  </Text>
                  <Text style={s.previewHeroDesc}>חלוקה לנושאים, סדר לימוד, ויעדי ביצוע לכל שלב במסלול.</Text>
                </View>
                <View style={s.previewMetricColumn}>
                  <View style={s.previewMetricItem}>
                    <Text style={s.previewMetricNumber}>{timePerSession === '5' ? '4' : timePerSession === '20' ? '16' : '9'}</Text>
                    <Text style={s.previewMetricLabel}>תרגולים ותרחישים ממוקדים</Text>
                  </View>
                  <View style={s.previewMetricItem}>
                    <Text style={s.previewMetricNumber}>AI</Text>
                    <Text style={s.previewMetricLabel}>ליווי מבוסס החומר שלך</Text>
                  </View>
                </View>
              </View>
              <View style={s.previewSupportRow}>
                <View style={s.previewSupportItem}>
                  <Text style={s.previewSupportTitle}>סקירת טעויות וחזרה</Text>
                  <Text style={s.previewSupportDesc}>לופ תרגול חוזר לנקודות חלשות עד שיפור יציב.</Text>
                </View>
                <View style={s.previewSupportItem}>
                  <Text style={s.previewSupportTitle}>מדדי התקדמות ומוכנות</Text>
                  <Text style={s.previewSupportDesc}>תמונת מצב ברורה של מה כבר מוכן ומה דורש עוד חיזוק.</Text>
                </View>
              </View>
              <View style={s.previewMetaRow}>
                <Text style={s.previewMetaItem}>נושא: {topic}</Text>
                {level && <Text style={s.previewMetaItem}>רמה: {LEVEL_OPTIONS.find(o => o.value === level)?.label}</Text>}
                <Text style={s.previewMetaItem}>שפה: {LANGUAGE_OPTIONS.find(o => o.value === language)?.label}</Text>
              </View>
            </View>

            <View style={s.contentTrustStrip}>
              <Text style={s.contentTrustText}>לא חייבים להתחיל מחומר מושלם — ניצור מבנה ראשוני שאפשר לערוך ולדייק אחר כך.</Text>
            </View>

            <View style={s.contentActions}>
              <Pressable style={s.contentBackBtn} onPress={handleBack} accessibilityRole="button">
                <Text style={s.contentBackBtnText}>חזרה</Text>
              </Pressable>
              <ClickSpark onPress={handleNext} disabled={submitting}>
                <View style={[s.contentPrimaryBtn, submitting && s.primaryBtnDisabled]}>
                  {submitting
                    ? <ActivityIndicator color="#fff" size="small" />
                    : <Text style={s.contentPrimaryBtnText}>{isAuthenticated ? 'צור חוויית למידה' : 'התחבר כדי ליצור מסלול'}</Text>}
                </View>
              </ClickSpark>
            </View>
          </View>
        )}

        {error && <ErrorBox msg={error} />}

        <View style={{ height: 48 }} />
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  container: {
    backgroundColor: PREMIUM_BG,
    paddingTop: Platform.OS === 'web' ? 32 : 48,
    paddingBottom: 40,
    paddingHorizontal: 20,
    maxWidth: 940,
    alignSelf: 'center' as any,
    width: '100%',
    gap: 20,
    position: 'relative',
  },
  bgLayer: {
    ...StyleSheet.absoluteFillObject,
  },
  topGlow: {
    position: 'absolute',
    top: -80,
    right: -60,
    width: 420,
    height: 320,
    borderRadius: 220,
  },
  bottomGlow: {
    position: 'absolute',
    bottom: 120,
    left: -70,
    width: 340,
    height: 260,
    borderRadius: 180,
  },

  header: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 14,
    marginBottom: 2,
  },
  closeBtn: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: '#121A31', borderWidth: 1, borderColor: PREMIUM_BORDER,
    alignItems: 'center', justifyContent: 'center',
  },
  closeBtnText: { color: '#D6E0FF', fontSize: 16, fontFamily: theme.fonts.bold },
  headerTitle: { fontFamily: theme.fonts.bold, fontSize: 36, color: '#F5F8FF', textAlign: 'right', lineHeight: 44, marginTop: 2 },
  headerSub: { fontFamily: theme.fonts.medium, fontSize: 13, color: '#A3B2D7', textAlign: 'right' },
  headerHint: { fontFamily: theme.fonts.regular, fontSize: 15, color: '#CAD4EF', textAlign: 'right', lineHeight: 22, marginTop: 8, maxWidth: 820 },

  stepBody: { gap: 14 },
  stepQuestion: { fontFamily: theme.fonts.bold, fontSize: 22, color: TEXT, textAlign: 'right', lineHeight: 30 },
  stepHint: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right', lineHeight: 20 },

  fieldGroup: { gap: 8 },
  fieldLabel: { fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT, textAlign: 'right' },
  premiumCard: {
    backgroundColor: PREMIUM_CARD,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: PREMIUM_BORDER,
    paddingHorizontal: 28,
    paddingVertical: 28,
    gap: 20,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 30px 80px rgba(2,8,24,0.55)' } as any) : {}),
  },
  heroInput: {
    backgroundColor: PREMIUM_CARD_2,
    color: '#F4F7FF',
    borderWidth: 1.5,
    borderColor: '#364878',
    borderRadius: 18,
    paddingHorizontal: 18,
    paddingVertical: 18,
    fontFamily: theme.fonts.regular,
    fontSize: 16,
    lineHeight: 26,
    minHeight: 190,
    textAlignVertical: 'top',
  },
  outputPreview: {
    backgroundColor: '#0C1326',
    borderWidth: 1,
    borderColor: '#2B3B62',
    borderRadius: 16,
    padding: 16,
    gap: 12,
  },
  outputTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 17,
    color: '#F6F8FF',
    textAlign: 'right',
  },
  outputGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 10,
  },
  outputCell: {
    flexBasis: Platform.OS === 'web' ? '48.5%' : '100%',
    backgroundColor: '#111B35',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#2D3E65',
    padding: 12,
    gap: 4,
  },
  outputCellIcon: { fontSize: 16, textAlign: 'right' },
  outputCellTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 14,
    color: '#EFF4FF',
    textAlign: 'right',
    lineHeight: 20,
  },
  outputCellDesc: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: '#AAB6D8',
    textAlign: 'right',
    lineHeight: 18,
  },
  trustStrip: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#33446C',
    backgroundColor: '#11182E',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  trustText: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#C4D0EE',
    textAlign: 'right',
    lineHeight: 20,
  },
  step1CtaRow: {
    flexDirection: 'row-reverse',
    gap: 12,
    marginTop: 6,
  },
  step1PrimaryBtn: {
    minHeight: 52,
    borderRadius: 14,
    backgroundColor: PRIMARY,
    paddingHorizontal: 22,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 240,
  },
  step1PrimaryBtnText: {
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    color: '#fff',
  },
  primaryBtnDisabled: { opacity: 0.45 },
  step1BackBtn: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#34446D',
    backgroundColor: '#151E35',
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  step1BackBtnText: {
    fontFamily: theme.fonts.medium,
    fontSize: 15,
    color: '#CAD4EE',
  },
  levelStepCard: {
    backgroundColor: '#0F1730',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#2C3B62',
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 18,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 28px 70px rgba(2,8,24,0.50)' } as any) : {}),
  },
  levelSectionHeader: {
    gap: 7,
  },
  levelStepLabel: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#A9B8DE',
    textAlign: 'right',
  },
  levelStepTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 31,
    color: '#F7F9FF',
    textAlign: 'right',
    lineHeight: 40,
  },
  levelStepHint: {
    fontFamily: theme.fonts.regular,
    fontSize: 15,
    color: '#CAD6F2',
    textAlign: 'right',
    lineHeight: 23,
    maxWidth: 720,
    alignSelf: 'flex-end',
  },
  levelCardsWrap: {
    gap: 12,
  },
  levelGoalSection: {
    gap: 8,
    marginTop: 2,
  },
  levelGoalLabel: {
    fontFamily: theme.fonts.medium,
    fontSize: 14,
    color: '#CBD6F2',
    textAlign: 'right',
  },
  levelGoalInput: {
    backgroundColor: '#101B37',
    color: '#F7F9FF',
    borderWidth: 1,
    borderColor: '#2E3C63',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 13,
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    minHeight: 52,
    textAlignVertical: 'center',
  },
  levelReassurance: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#32456E',
    backgroundColor: '#111A34',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  levelReassuranceText: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#B8C7EB',
    textAlign: 'right',
  },
  levelActions: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
    marginTop: 4,
  },
  levelBackBtn: {
    minHeight: 54,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#34466F',
    backgroundColor: '#172039',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    paddingHorizontal: 18,
  },
  levelBackBtnText: {
    fontFamily: theme.fonts.medium,
    fontSize: 15,
    color: '#C6D3F1',
  },
  levelNextBtn: {
    minHeight: 54,
    borderRadius: 14,
    backgroundColor: PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 260,
    paddingHorizontal: 22,
  },
  levelNextBtnText: {
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    color: '#fff',
  },
  timeStepCard: {
    backgroundColor: '#0E162D',
    borderRadius: 28,
    borderWidth: 1,
    borderColor: '#2A3A62',
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 18,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 34px 84px rgba(2,8,24,0.56)' } as any) : {}),
  },
  timeStepHeader: {
    gap: 7,
  },
  timeStepLabel: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#A7B6DC',
    textAlign: 'right',
  },
  timeStepTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 32,
    color: '#F8FAFF',
    textAlign: 'right',
    lineHeight: 40,
  },
  timeStepHint: {
    fontFamily: theme.fonts.regular,
    fontSize: 15,
    color: '#C6D3F1',
    textAlign: 'right',
    lineHeight: 23,
    maxWidth: 720,
    alignSelf: 'flex-end',
  },
  timeCardsWrap: {
    gap: 12,
  },
  timeReassurance: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#32456C',
    backgroundColor: '#111A33',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  timeReassuranceText: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#B7C4E5',
    textAlign: 'right',
    lineHeight: 20,
  },
  timeActions: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
    marginTop: 2,
  },
  timeBackBtn: {
    minHeight: 56,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#354671',
    backgroundColor: '#172038',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    paddingHorizontal: 18,
  },
  timeBackBtnText: {
    fontFamily: theme.fonts.medium,
    fontSize: 15,
    color: '#C8D3EE',
  },
  timeNextBtn: {
    minHeight: 56,
    borderRadius: 14,
    backgroundColor: PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 290,
    paddingHorizontal: 24,
  },
  timeNextBtnText: {
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    color: '#fff',
  },
  contentStepCard: {
    backgroundColor: '#0B1224',
    borderRadius: 30,
    borderWidth: 1,
    borderColor: '#2B3A63',
    paddingHorizontal: 28,
    paddingVertical: 28,
    gap: 18,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 36px 90px rgba(2,8,24,0.60)' } as any) : {}),
  },
  contentStepHeader: { gap: 8 },
  contentStepLabel: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#9FB0D9',
    textAlign: 'right',
  },
  contentStepTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 34,
    color: '#F7FAFF',
    textAlign: 'right',
    lineHeight: 42,
  },
  contentStepHint: {
    fontFamily: theme.fonts.regular,
    fontSize: 15,
    color: '#CAD7F3',
    textAlign: 'right',
    lineHeight: 23,
    maxWidth: 740,
    alignSelf: 'flex-end',
  },
  authCheckpointCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: `${PRIMARY}66`,
    backgroundColor: `${PRIMARY}18`,
    padding: 14,
    gap: 8,
  },
  authCheckpointTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 15,
    color: '#FFFFFF',
    textAlign: 'right',
  },
  authCheckpointBody: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: '#D6E1FB',
    textAlign: 'right',
    lineHeight: 20,
  },
  authCheckpointBtn: {
    alignSelf: 'flex-end',
    borderRadius: 10,
    backgroundColor: PRIMARY,
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  authCheckpointBtnText: {
    fontFamily: theme.fonts.bold,
    fontSize: 13,
    color: '#fff',
  },
  contentModeSelector: {
    flexDirection: 'row-reverse',
    gap: 12,
  },
  contentModeCard: {
    flex: 1,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#344770',
    backgroundColor: '#111B35',
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 5,
  },
  contentModeCardActive: {
    borderColor: '#8F8AFF',
    backgroundColor: '#1A2450',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 12px 28px rgba(8,12,28,0.45)' } as any) : {}),
  },
  contentModeTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    color: '#E4ECFF',
    textAlign: 'right',
  },
  contentModeTitleActive: { color: '#FFFFFF' },
  contentModeDesc: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: '#ABBBE0',
    textAlign: 'right',
    lineHeight: 18,
  },
  contentModeHelper: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#AAB8DC',
    textAlign: 'right',
    marginTop: -2,
  },
  contentInputSection: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#31436C',
    backgroundColor: '#0F1932',
    padding: 16,
    gap: 10,
  },
  contentInputLabel: {
    fontFamily: theme.fonts.bold,
    fontSize: 15,
    color: '#F4F8FF',
    textAlign: 'right',
  },
  contentTextarea: {
    backgroundColor: '#111E3D',
    color: '#F7FAFF',
    borderWidth: 1.5,
    borderColor: '#3A4F7D',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 16,
    fontFamily: theme.fonts.regular,
    fontSize: 15,
    lineHeight: 24,
    minHeight: 240,
    textAlignVertical: 'top',
  },
  charCountRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  charCount: { fontFamily: theme.fonts.medium, fontSize: 12, color: '#AAB7D9' },
  charCountHint: { fontFamily: theme.fonts.medium, fontSize: 12 },

  // PDF area — shared container
  pdfArea: {
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: '#3A4F7D',
    borderStyle: 'dashed' as any,
    overflow: 'hidden',
    minHeight: 220,
    backgroundColor: '#111E3D',
  },

  // PDF drop zone (default state)
  pdfDropZone: {
    padding: 32,
    alignItems: 'center',
    gap: 9,
    flex: 1,
    justifyContent: 'center',
  },
  pdfDropIcon: { fontSize: 38 },
  pdfDropTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: '#EFF4FF', textAlign: 'center' },
  pdfDropHint: { fontFamily: theme.fonts.regular, fontSize: 13, color: '#B0BFE2', textAlign: 'center' },

  // PDF error banner (inside drop zone on error)
  pdfErrorBanner: {
    marginTop: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#7F1D1D',
    backgroundColor: '#3B0A0A',
    padding: 12,
    width: '100%',
    gap: 4,
    alignItems: 'center',
  },
  pdfErrorIcon: { fontSize: 18, color: '#FCA5A5' },
  pdfErrorText: { fontFamily: theme.fonts.medium, fontSize: 13, color: '#FCA5A5', textAlign: 'center' },
  pdfErrorAction: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED, textAlign: 'center' },

  // PDF loading state
  pdfLoadingState: {
    padding: 42,
    alignItems: 'center',
    gap: 10,
  },
  pdfLoadingTitle: { fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'center' },
  pdfLoadingHint: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MUTED, textAlign: 'center' },

  // PDF success state
  pdfSuccessState: {
    padding: 28,
    alignItems: 'center',
    gap: 8,
  },
  pdfSuccessIcon: {
    width: 52, height: 52, borderRadius: 26,
    backgroundColor: `${GREEN}22`,
    alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' ? ({ boxShadow: `0 0 20px ${GREEN}33` } as any) : {}),
  },
  pdfSuccessTitle: { fontFamily: theme.fonts.bold, fontSize: 16, color: GREEN, textAlign: 'center' },
  pdfSuccessMsg: { fontFamily: theme.fonts.medium, fontSize: 13, color: '#D4E0FB', textAlign: 'center' },
  pdfSuccessHint: { fontFamily: theme.fonts.regular, fontSize: 12, color: '#AAB9DA', textAlign: 'center', marginTop: 4 },
  pdfReplaceBtn: {
    marginTop: 8,
    paddingHorizontal: 16, paddingVertical: 9,
    borderRadius: 10, borderWidth: 1, borderColor: '#415784',
    backgroundColor: '#1A294C',
  },
  pdfReplaceBtnText: { fontFamily: theme.fonts.medium, fontSize: 13, color: '#D5DFFF' },

  // Value promise (step 1)
  valuePromise: {
    backgroundColor: SURFACE2,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: `${PRIMARY}33`,
    padding: 14,
    gap: 8,
    marginTop: 4,
  },
  valuePromiseTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 12,
    color: TEXT_MUTED,
    textAlign: 'right',
    textTransform: 'uppercase' as any,
    letterSpacing: 0.8,
    marginBottom: 2,
  },
  valuePromiseRow: {
    flexDirection: 'row-reverse',
    gap: 8,
  },
  valuePromiseItem: {
    flex: 1,
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: TEXT_MED,
    textAlign: 'right',
  },

  transformationPreview: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#344A76',
    backgroundColor: '#111D39',
    padding: 16,
    gap: 12,
  },
  transformationTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 17,
    color: '#F4F8FF',
    textAlign: 'right',
  },
  previewTopRow: {
    flexDirection: Platform.OS === 'web' ? 'row-reverse' : 'column',
    gap: 10,
  },
  previewHeroBlock: {
    flex: 1.2,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#435B8A',
    backgroundColor: '#16264A',
    padding: 14,
    gap: 6,
  },
  previewHeroKicker: {
    fontFamily: theme.fonts.medium,
    fontSize: 11,
    color: '#AFC0E9',
    textAlign: 'right',
    textTransform: 'uppercase' as any,
    letterSpacing: 0.6,
  },
  previewHeroTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 18,
    color: '#FFFFFF',
    textAlign: 'right',
    lineHeight: 26,
  },
  previewHeroDesc: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: '#C5D3F2',
    textAlign: 'right',
    lineHeight: 20,
  },
  previewMetricColumn: {
    flex: 0.9,
    gap: 10,
  },
  previewMetricItem: {
    flex: 1,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#3A4F7C',
    backgroundColor: '#152344',
    padding: 12,
    justifyContent: 'center',
    gap: 4,
  },
  previewMetricNumber: {
    fontFamily: theme.fonts.bold,
    fontSize: 23,
    color: '#FFFFFF',
    textAlign: 'right',
  },
  previewMetricLabel: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#D0DBF6',
    textAlign: 'right',
    lineHeight: 19,
  },
  previewSupportRow: {
    flexDirection: Platform.OS === 'web' ? 'row-reverse' : 'column',
    gap: 10,
  },
  previewSupportItem: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334771',
    backgroundColor: '#13203E',
    padding: 12,
    gap: 4,
  },
  previewSupportTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 14,
    color: '#EFF4FF',
    textAlign: 'right',
  },
  previewSupportDesc: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: '#B4C3E5',
    textAlign: 'right',
    lineHeight: 18,
  },
  previewMetaRow: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: '#314367',
    paddingTop: 10,
  },
  previewMetaItem: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#3A4F7A',
    backgroundColor: '#16233F',
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: '#ADC0E8',
  },
  contentTrustStrip: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#384C75',
    backgroundColor: '#101A33',
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  contentTrustText: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: '#C5D2EE',
    textAlign: 'right',
    lineHeight: 20,
  },
  contentActions: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
    marginTop: 4,
  },
  contentBackBtn: {
    minHeight: 58,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#364A73',
    backgroundColor: '#17223F',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    paddingHorizontal: 18,
  },
  contentBackBtnText: {
    fontFamily: theme.fonts.medium,
    fontSize: 16,
    color: '#CAD5F0',
  },
  contentPrimaryBtn: {
    minHeight: 58,
    borderRadius: 14,
    backgroundColor: PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 320,
    paddingHorizontal: 26,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 16px 34px rgba(90,74,255,0.42)' } as any) : {}),
  },
  contentPrimaryBtnText: {
    fontFamily: theme.fonts.bold,
    fontSize: 17,
    color: '#fff',
  },
});
